import { Box, Button, Stack } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { stableStringify } from '@provision/formatting-utils/dist/utils';
import { HighlightCssClass } from '@provision/rich-text-editor';
import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { GenericForm } from 'src/components';
import {
  Address,
  PartnerGroupRules,
  useAddress,
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { useAutoGenerateSiteDocs } from 'src/graphql/entities/auto-generate-site-docs/use-auto-generate-site-docs';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import useSiteViewsStore from 'src/store/site-views';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import useResizeObserver from 'use-resize-observer';
import {
  FilesSection,
  SiteOverviewSection,
  SubSystemsSection,
  SystemInfrastructureSection,
} from './components/sections';
import {
  createDefaultAccordionState,
  MIN_WIDTH_FOR_SPLIT_LAYOUT,
  ORG_NOTES_FIELD_NAME,
  SITE_DOCUMENTATION_ACCORDION_KEYS,
  SiteDocumentationAccordionSectionKey,
  SiteDocumentationAccordionState,
} from './site-documentation.constants';
import { normalizeOrgNotes } from './site-documentation.helpers';
import {
  AIFormGlow,
  BottomActionButtons,
  MainColumn,
  SectionsLayout,
} from './site-documentation.styles';
import {
  AutoGeneratedFields,
  DocumentationFormField,
  DocumentationFormValue,
  DocumentationFormValues,
  SiteDocumentationProps,
} from './site-documentation.types';
import { subSystems, systemInfrastructure } from './sub-systems-const';
import { useCommonProps } from './use-site-documentation-form';

const documentationFields = [
  ORG_NOTES_FIELD_NAME,
  ...systemInfrastructure.map((field) => field.name),
  ...subSystems.flatMap((subSystem) => subSystem.fields.map((field) => field.name)),
];

const getFormDefaultValues = (data: Address): DocumentationFormValues =>
  documentationFields.reduce((acc, field) => {
    return { ...acc, [field]: data[field as keyof Address] };
  }, {}) as DocumentationFormValues;

export const SiteDocumentation = ({
  conversation,
  address,
  hasShowMore = true,
  fullSizeView = false,
}: SiteDocumentationProps): ReactElement => {
  const [systemInfrastructureShowMore, setSystemInfrastructureShowMore] = useState(
    hasShowMore ? false : true,
  );
  const [subSystemsShowMore, setSubSystemsShowMore] = useState(hasShowMore ? false : true);
  const [autoGeneratedFields, setAutoGeneratedFields] = useState<AutoGeneratedFields>({});
  const [formAutoSave, setFormAutoSave] = useState(true);
  const [isAutoFillLoading, setIsAutoFillLoading] = useState(false);
  const [isFormSaving, setIsFormSaving] = useState(false);
  const [filesCount, setFilesCount] = useState(0);

  const { showErrorAlert } = useNotifications();

  const { updateAddress } = useAddress(address?.addressId);

  const { autoGenerateSiteDocs } = useAutoGenerateSiteDocs();

  const { groupRules } = useGroupRules();

  const formDefaultValues = useMemo(() => getFormDefaultValues(address as Address), [address]);

  const formMethods = useForm<DocumentationFormValues>({
    defaultValues: formDefaultValues,
    mode: 'all',
  });

  const { formState, watch } = formMethods;

  const { errors, isValid } = formState;

  const watchedValues = watch();

  const dirtyFields = useMemo(
    () => ({ ...formState.dirtyFields }),
    // dirtyFields is been mutated inside useForm but we need to react to every change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stableStringify(formState.dirtyFields)],
  );

  const { partnerGroupRules } = usePartnerGroupRules(address?.partnerId);

  const accessToUpdateSiteDocumentation = Boolean(
    groupRules[UserGroupRules.AccessToUpdateSiteDocumentation],
  );

  const conversationStatus = conversation?.conversationStatusId as
    | ConversationStatus
    | undefined;

  const hasAccessToAutofillSiteDocumentation =
    Boolean(partnerGroupRules[PartnerGroupRules.AccessToAutofillSiteDocumentation]) ||
    Boolean(groupRules[UserGroupRules.AccessToAutofillSiteDocumentation]);

  const canEditSiteDocumentation = useMemo(
    () =>
      accessToUpdateSiteDocumentation && !address?.isDeleted &&
      (conversationStatus === undefined || conversationStatus !== ConversationStatus.Closed),
    [accessToUpdateSiteDocumentation, address?.isDeleted, conversationStatus],
  );

  const isFormDisabled = !canEditSiteDocumentation || isAutoFillLoading;

  const commonProps = useCommonProps(isFormDisabled);

  const siteDocumentationSystemInfrastructureFormFields = useMemo(() => {
    return systemInfrastructure.map(
      (field: DocumentationFormField) =>
        ({
          ...field,
          ...commonProps,
        }) as DocumentationFormField,
    );
  }, [commonProps]);

  const { user } = useCurrentUserProfile();

  const storeKey = user?.id ?? 'default';
  const accordionStateMap = useSiteViewsStore(
    (state) => state.siteDocumentationAccordionState,
  );
  const setAccordionStateInStore = useSiteViewsStore(
    (state) => state.setSiteDocumentationAccordionState,
  );

  const createAccordionStateWithDefaults = useCallback(
    (state?: SiteDocumentationAccordionState): SiteDocumentationAccordionState => {
      const base = createDefaultAccordionState();

      if (!state) {
        return base;
      }

      SITE_DOCUMENTATION_ACCORDION_KEYS.forEach((key) => {
        base[key] = state[key] ?? base[key];
      });

      return base;
    },
    [],
  );

  const normalizedStoredAccordionState = useMemo(
    () => createAccordionStateWithDefaults(accordionStateMap[storeKey]),
    [accordionStateMap, createAccordionStateWithDefaults, storeKey],
  );

  const [accordionState, setAccordionState] = useState<SiteDocumentationAccordionState>(
    () => ({ ...normalizedStoredAccordionState }),
  );

  useEffect(() => {
    setAccordionState((prev) => {
      const hasDifference = SITE_DOCUMENTATION_ACCORDION_KEYS.some(
        (key) => prev[key] !== normalizedStoredAccordionState[key],
      );

      return hasDifference ? { ...normalizedStoredAccordionState } : prev;
    });
  }, [normalizedStoredAccordionState]);

  useEffect(() => {
    const hasDifference = SITE_DOCUMENTATION_ACCORDION_KEYS.some(
      (key) => accordionState[key] !== normalizedStoredAccordionState[key],
    );

    if (hasDifference) {
      setAccordionStateInStore(storeKey, accordionState);
    }
  }, [accordionState, normalizedStoredAccordionState, setAccordionStateInStore, storeKey]);

  const handleAccordionChange = useCallback(
    (section: SiteDocumentationAccordionSectionKey) =>
      (_event: SyntheticEvent, expanded: boolean) => {
        setAccordionState((prev) => ({
          ...prev,
          [section]: expanded,
        }));
      },
    [],
  );

  const [isStackedLayout, setIsStackedLayout] = useState(true);
  const { ref: containerRef, width: containerWidth } = useResizeObserver<HTMLDivElement>();

  useEffect(() => {
    if (typeof containerWidth !== 'number') {
      return;
    }

    const shouldStack = containerWidth < MIN_WIDTH_FOR_SPLIT_LAYOUT;
    setIsStackedLayout((prev) => (prev === shouldStack ? prev : shouldStack));
  }, [containerWidth]);

  const hasText = (s?: string): boolean => !!s && s.replace(/<[^>]*>/g, '').trim() !== '';

  const saveForm = useCallback(
    (values: DocumentationFormValues) => {
      if (!address?.addressId) {
        return;
      }

      setIsFormSaving(true);

      const normalizedValues = { ...values };

      if (normalizedValues[ORG_NOTES_FIELD_NAME]) {
        normalizedValues[ORG_NOTES_FIELD_NAME] = normalizeOrgNotes(
          normalizedValues[ORG_NOTES_FIELD_NAME] as string,
        );
      }

      const patchedAddress = {
        ...address,
        ...normalizedValues,
      } as Address;
      if (!hasText(patchedAddress.rsmNotes)) {
        patchedAddress.rsmNotes = '';
      }

      return updateAddress(patchedAddress)
        .then(() => formMethods.reset(getFormDefaultValues(patchedAddress)))
        .finally(() => setIsFormSaving(false));
    },
    [address, formMethods, updateAddress],
  );

  const onAutoFillClicked = useCallback(async () => {
    const addressId = address?.addressId;
    if (!addressId) {
      return;
    }

    setIsAutoFillLoading(true);

    try {
      const result = await autoGenerateSiteDocs({ addressId });

      if (!result) {
        throw new Error();
      }

      Object.keys(result).forEach((fieldKey) => {
        const value = result[fieldKey as keyof AutoGeneratedFields] as string;

        if (!value || !documentationFields.includes(fieldKey)) {
          return;
        }

        if (fieldKey === ORG_NOTES_FIELD_NAME) {
          formMethods?.setValue(
            fieldKey,
            `${hasText(address.orgNotes) ? `${address.orgNotes} ` : ''}<span class="${HighlightCssClass}">${value}</span>`,
            {
              shouldDirty: true,
            },
          );
        } else {
          formMethods?.setValue(fieldKey, value, { shouldDirty: true });
        }
      });
      setAutoGeneratedFields(result);
      setFormAutoSave(false);
    } catch {
      showErrorAlert('Failed to auto-fill site documentation. Please try again.');
    } finally {
      setIsAutoFillLoading(false);
    }
  }, [
    address?.addressId,
    address.orgNotes,
    autoGenerateSiteDocs,
    formMethods,
    showErrorAlert,
  ]);

  const onFieldChange = useCallback(
    (fieldName: string, value: DocumentationFormValue) => {
      if (formAutoSave && !errors[fieldName]) {
        void saveForm({ [fieldName]: value });
      }
    },
    [errors, formAutoSave, saveForm],
  );

  const onResetField = useCallback(
    (fieldName: string) => {
      formMethods?.resetField(fieldName);
    },
    [formMethods],
  );

  const numberOfDirtyFields = useMemo(() => {
    return Object.keys(dirtyFields).reduce((acc, field) => {
      if (dirtyFields[field]) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [dirtyFields]);

  useEffect(() => {
    if (numberOfDirtyFields === 0 && !formAutoSave) {
      setFormAutoSave(true);
      setAutoGeneratedFields(null);
    }
  }, [numberOfDirtyFields, formAutoSave]);

  const handleCancelFormChanges = useCallback(() => {
    formMethods.reset();
    setFormAutoSave(true);
    setAutoGeneratedFields(null);
  }, [formMethods]);

  const handleSaveFormChanges = useCallback(
    async (values: DocumentationFormValues) => {
      if (isValid) {
        await saveForm(values);
        setAutoGeneratedFields(null);
        setFormAutoSave(true);
      }
    },
    [isValid, saveForm],
  );

  const createShouldShowField = useCallback(
    (showMoreValue: boolean) => (field: DocumentationFormField) => {
      const showAll = hasShowMore ? showMoreValue : true;
      return (
        showAll ||
        field.alwaysVisible ||
        (!!formMethods.getValues(field.name) && !field.showOnlyWithCondition)
      );
    },
    [formMethods, hasShowMore],
  );

  const shouldShowSystemInfrastructureField = useMemo(
    () => createShouldShowField(systemInfrastructureShowMore),
    [createShouldShowField, systemInfrastructureShowMore],
  );

  const shouldShowSubSystemField = useMemo(
    () => createShouldShowField(subSystemsShowMore),
    [createShouldShowField, subSystemsShowMore],
  );

  const isFieldVisibleWhenCollapsed = createShouldShowField(false);
  const isFieldVisibleWhenExpanded = createShouldShowField(true);

  const systemInfrastructureHasHiddenFields =
    hasShowMore &&
    siteDocumentationSystemInfrastructureFormFields.some(
      (field) => !isFieldVisibleWhenCollapsed(field) && isFieldVisibleWhenExpanded(field),
    );

  const subSystemsHasHiddenFields =
    hasShowMore &&
    subSystems.some((subSystem) =>
      subSystem.fields.some(
        (field) => !isFieldVisibleWhenCollapsed(field) && isFieldVisibleWhenExpanded(field),
      ),
    );

  const toggleSystemInfrastructureShowMore = useCallback(() => {
    setSystemInfrastructureShowMore((currentValue) => !currentValue);
  }, []);

  const toggleSubSystemsShowMore = useCallback(() => {
    setSubSystemsShowMore((currentValue) => !currentValue);
  }, []);

  const orgNotesHasAISuggestion = useMemo(() => {
    const currentValue = (watchedValues?.[ORG_NOTES_FIELD_NAME] as string) || '';
    const defaultValue = (formDefaultValues[ORG_NOTES_FIELD_NAME] as string) || '';

    return (
      !!autoGeneratedFields &&
      ORG_NOTES_FIELD_NAME in autoGeneratedFields &&
      currentValue !== defaultValue &&
      currentValue.includes(HighlightCssClass)
    );
  }, [autoGeneratedFields, formDefaultValues, watchedValues]);

  return (
    <AIFormGlow hasAIGlow={isAutoFillLoading}>
      <Stack ref={containerRef} sx={{ maxWidth: '100%' }} p={1} mt={1}>
        <GenericForm formMethods={formMethods}>
          <Stack spacing={2}>
            <SectionsLayout isStackedLayout={isStackedLayout}>
              <MainColumn spacing={2} isStackedLayout={isStackedLayout}>
                <SiteOverviewSection
                  expanded={accordionState.siteOverview}
                  onChange={handleAccordionChange('siteOverview')}
                  orgNotesHasAISuggestion={orgNotesHasAISuggestion}
                  isFormDisabled={isFormDisabled}
                  onResetField={onResetField}
                  onFieldChange={onFieldChange}
                />
                {isStackedLayout ? (
                  <FilesSection
                    asAside={false}
                    expanded={accordionState.files}
                    onChange={handleAccordionChange('files')}
                    filesCount={filesCount}
                    onFileCountChange={setFilesCount}
                    isFormDisabled={isFormDisabled}
                    address={address}
                    hasAccessToAutofillSiteDocumentation={hasAccessToAutofillSiteDocumentation}
                    onAutoFillClicked={onAutoFillClicked}
                    isAutoFillLoading={isAutoFillLoading}
                  />
                ) : null}
                <SystemInfrastructureSection
                  expanded={accordionState.systemInfrastructure}
                  onChange={handleAccordionChange('systemInfrastructure')}
                  fields={siteDocumentationSystemInfrastructureFormFields}
                  shouldShowField={shouldShowSystemInfrastructureField}
                  commonProps={commonProps}
                  onFieldChange={onFieldChange}
                  onResetField={onResetField}
                  autoGeneratedFields={autoGeneratedFields}
                  defaultValues={formDefaultValues}
                  getFormValues={formMethods.getValues}
                  dirtyFields={dirtyFields}
                  watchedValues={watchedValues}
                  hasShowMore={hasShowMore}
                  showMore={systemInfrastructureShowMore}
                  hasHiddenFields={systemInfrastructureHasHiddenFields}
                  toggleShowMore={toggleSystemInfrastructureShowMore}
                />
                <SubSystemsSection
                  expanded={accordionState.subSystems}
                  onChange={handleAccordionChange('subSystems')}
                  shouldShowField={shouldShowSubSystemField}
                  commonProps={commonProps}
                  onFieldChange={onFieldChange}
                  onResetField={onResetField}
                  autoGeneratedFields={autoGeneratedFields}
                  defaultValues={formDefaultValues}
                  getFormValues={formMethods.getValues}
                  dirtyFields={dirtyFields}
                  watchedValues={watchedValues}
                  hasShowMore={hasShowMore}
                  showMore={subSystemsShowMore}
                  hasHiddenFields={subSystemsHasHiddenFields}
                  toggleShowMore={toggleSubSystemsShowMore}
                />
              </MainColumn>
              {!isStackedLayout ? (
                <FilesSection
                  asAside
                  expanded={accordionState.files}
                  onChange={handleAccordionChange('files')}
                  filesCount={filesCount}
                  onFileCountChange={setFilesCount}
                  isFormDisabled={isFormDisabled}
                  address={address}
                  hasAccessToAutofillSiteDocumentation={hasAccessToAutofillSiteDocumentation}
                  onAutoFillClicked={onAutoFillClicked}
                  isAutoFillLoading={isAutoFillLoading}
                />
              ) : null}
            </SectionsLayout>
            {!!numberOfDirtyFields && !formAutoSave && (
              <BottomActionButtons bottom={fullSizeView ? 0 : '-8px'}>
                <Box flexGrow={1} textAlign="center" fontSize="0.875rem">
                  Auto-fill completed. {numberOfDirtyFields}{' '}
                  {numberOfDirtyFields === 1 ? 'field was' : 'fields were'} updated.
                </Box>
                <Stack direction="row" spacing={1} flexGrow={1} justifyContent="center">
                  <Button
                    size="small"
                    disabled={isFormSaving || isFormDisabled}
                    onClick={handleCancelFormChanges}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    disabled={isFormSaving || isFormDisabled}
                    variant="contained"
                    onClick={formMethods.handleSubmit(handleSaveFormChanges)}
                  >
                    {isFormSaving ? 'Saving...' : 'Save changes'}
                  </Button>
                </Stack>
              </BottomActionButtons>
            )}
          </Stack>
        </GenericForm>
      </Stack>
    </AIFormGlow>
  );
};
